import React, { FC } from 'react';
import { FormProvider, useWatch } from 'react-hook-form';

import { translate } from '@utils/locale';
import { AutocompleteDropdown, CustomSwitch, DualButton } from '@components';
import { OpportunityType } from '@modules/companies/create-company/constants';
import { PreIPODealStatus } from '@constants/common';
import { colors } from '@constants/colors';

import { PreIPODealConfigurationProps } from '../../types';
import { isPreIPOBottomActionDisabled } from '../../common';

const PreIPODealConfiguration: FC<PreIPODealConfigurationProps> = ({
  methods,
  opportunityList,
  errors,
  handleNextClick,
  isUpdatePreIPOLoading,
  handleSaveAsDraftClick
}) => {
  const { control, setValue, getValues } = methods;

  const [soldOut, displayNewTag] = useWatch({
    control: control,
    name: ['soldOut', 'displayNewTag']
  });

  const opportunityValues = opportunityList?.map(val => ({
    id: val.opportunity_id ?? '',
    name: `${val.company_name} (${val.subscriber_count} Subscribers)`,
    namePrefixElement: val.company_logo ? (
      <img src={val.company_logo} alt="" width="50px" />
    ) : (
      <div className="flex justify-center items-center mr-4 w-[50px] h-[50px] bg-aquaHaze rounded-md">
        {val.company_name?.[0]}
      </div>
    )
  }));
  return (
    <>
      <div className="px-5 pt-10">
        <FormProvider {...methods}>
          <form>
            <div className="flex flex-col">
              <div className="flex flex-row w-full">
                <div className="grid grid-cols-[auto_auto] grid-flow-row w-[60%] border border-lightGrey ">
                  <div className="py-4 px-6 text-base font-medium leading-6 bg-aquaHaze">
                    {translate(
                      'preIpoDeals.createPreIPODeals.preIPOConfig.widgets'
                    )}
                  </div>
                  <div className="py-4 pr-6 pl-3 text-base font-medium leading-6 bg-aquaHaze">
                    {translate(
                      'preIpoDeals.createPreIPODeals.preIPOConfig.toggle'
                    )}
                  </div>
                  {/* Sold-Out toggle */}
                  <div className="flex flex-col justify-center items-start p-6 text-sm leading-6 border-b border-b-lightGrey">
                    {translate(
                      'preIpoDeals.createPreIPODeals.preIPOConfig.soldOutTitle'
                    )}
                    <div className="text-xs text-grey whitespace-pre-wrap">
                      {translate(
                        'preIpoDeals.createPreIPODeals.preIPOConfig.soldOutDescription'
                      )}
                      <ul className="p-2 m-0 list-disc list-inside">
                        <li>
                          {translate(
                            'preIpoDeals.createPreIPODeals.preIPOConfig.soldOutDescription1'
                          )}
                        </li>
                        <li>
                          {translate(
                            'preIpoDeals.createPreIPODeals.preIPOConfig.soldOutDescription2'
                          )}
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="p-6 border-b border-b-lightGrey">
                    <CustomSwitch
                      control={control}
                      name={'soldOut'}
                      handleChange={event =>
                        setValue('soldOut', event.target.value === 'enable', {
                          shouldDirty: true
                        })
                      }
                      checked={soldOut}
                    />
                  </div>

                  {/* display new tag toggle */}
                  <div className="flex flex-col justify-center items-start p-6 text-sm leading-6 ">
                    {translate(
                      'preIpoDeals.createPreIPODeals.preIPOConfig.newTagTitle'
                    )}
                    <div className="text-xs text-grey whitespace-pre-wrap">
                      {translate(
                        'preIpoDeals.createPreIPODeals.preIPOConfig.newTagDescription'
                      )}
                    </div>
                  </div>
                  <div className="p-6 ">
                    <CustomSwitch
                      control={control}
                      name={'displayNewTag'}
                      handleChange={event =>
                        setValue(
                          'displayNewTag',
                          event.target.value === 'enable',
                          {
                            shouldDirty: true
                          }
                        )
                      }
                      checked={displayNewTag}
                    />
                  </div>
                </div>
              </div>
              {/* opportunity mapping */}
              <div className="invisible mt-5 mb-12 w-[60%] border border-lightGrey">
                <div className="py-4 px-6 w-full text-base font-medium leading-6 bg-aquaHaze">
                  {translate('deals.opportunityMapping.title')}
                </div>
                <div className="px-6 pt-2 pb-6">
                  <AutocompleteDropdown
                    control={control}
                    name={'linkedOpportunityId'}
                    placeholder={translate(
                      'deals.opportunityMapping.chooseOpportunity'
                    )}
                    options={opportunityValues}
                    errors={errors}
                    wrapperClass="pt-6 mr-6 w-full"
                    onChangeHandler={selectedItem => {
                      if (selectedItem) {
                        setValue('linkedOpportunityId', selectedItem.id, {
                          shouldDirty: true
                        });
                        setValue(
                          'linkedOpportunityType',
                          opportunityList.find(
                            val => val.opportunity_id === selectedItem.id
                          )?.opportunity_type as OpportunityType,
                          { shouldDirty: true }
                        );
                      } else {
                        setValue('linkedOpportunityId', null, {
                          shouldDirty: true
                        });
                        setValue('linkedOpportunityType', null, {
                          shouldDirty: true
                        });
                      }
                    }}
                    selectedItem={opportunityValues.find(
                      value =>
                        value.id === (getValues('linkedOpportunityId') ?? '')
                    )}
                    clearOnBlur={false}
                  />
                </div>
              </div>
            </div>
            <div className="fixed right-0 bottom-0 p-12 w-auto">
              <DualButton
                isPrimaryDisabled={
                  isPreIPOBottomActionDisabled(
                    getValues('status') as PreIPODealStatus
                  ) || getValues('status') === PreIPODealStatus.DRAFT
                }
                isSecondaryDisabled={isPreIPOBottomActionDisabled(
                  getValues('status') as PreIPODealStatus
                )}
                primaryButtonLabel={translate('next')}
                secondaryButtonLabel={translate('saveAsDraft')}
                onChangePrimaryButton={handleNextClick}
                isSecondaryButtonLoading={isUpdatePreIPOLoading}
                onChangeSecondaryButton={handleSaveAsDraftClick}
                secondaryButtonCustomStyle={{
                  backgroundColor: colors.gray88,
                  color: colors.black,
                  width: '136px'
                }}
              />
            </div>
          </form>
        </FormProvider>
      </div>
    </>
  );
};
export default PreIPODealConfiguration;
